import { IAuthenticationToken } from '@hsp/utilities';

import users from 'assets/data/fake/users.json';
import { defaultMockTime } from 'constants/index';
import { Uri } from 'constants/uri';
import { refreshToken } from 'services/utilities';
import { IForgotPassword, IUser, IUserLogin, IUserProfile } from 'types/user';

import { BaseHttpService } from './http/base-http.service';

class AuthenticationService extends BaseHttpService {
  constructor() {
    super();
  }

  extend(token: IAuthenticationToken): Promise<void> {
    const url = Uri.Authentication.Token.extend;

    return this.post(token, this.defaultRequestConfig(url));
  }

  login(data: IUserLogin): Promise<IAuthenticationToken> {
    return this.post(
      data,
      {
        url: Uri.Authentication.Token.login,
        withCredentials: true,
      },
      this.createInstance(false)
    );
  }

  logout(): Promise<void> {
    const url = Uri.Authentication.Token.revoke;

    return this.post(undefined, this.defaultRequestConfig(url));
  }

  postFakeForgetPwd(data: IForgotPassword): Promise<string> {
    return this.post(data, { url: Uri.PostFakePasswordForget });
  }

  postFakeProfile(data: IUserProfile): Promise<IUser> {
    return this.post(data, { url: Uri.PostEditProfile });
  }

  postFakeRegister(data: IUser): Promise<IUser> {
    const request = this.post(data, {
      url: Uri.PostFakeRegister,
    });

    return request
      ?.then((response) => response as IUser)
      .catch((e) => {
        let message = '';

        if (e.response && e.response.status) {
          switch (e.response.status) {
            case 404:
              message = 'Sorry! the page you are looking for could not be found';
              break;

            case 500:
              message = 'Sorry! something went wrong, please contact our support team';
              break;

            case 401:
              message = 'Invalid credentials';
              break;

            default:
              message = e[1];
              break;
          }
        }

        throw new Error(message);
      });
  }

  postJwtForgetPwd(data: IForgotPassword): Promise<string> {
    return this.post(data, { url: Uri.PostFakeJwtPasswordForget });
  }

  postJwtProfile(data: IUserProfile): Promise<IUser> {
    return this.post(data, { url: Uri.PostEditJwtProfile });
  }

  postJwtRegister(data: IUser): Promise<IUser> {
    const request = this.post(data, {
      url: Uri.PostFakeJwtRegister,
    });

    return request
      ?.then((response) => response as IUser)
      .catch((err) => {
        let message;

        if (err.response && err.response.status) {
          switch (err.response.status) {
            case 404:
              message = 'Sorry! the page you are looking for could not be found';
              break;

            case 500:
              message = 'Sorry! something went wrong, please contact our support team';
              break;

            case 401:
              message = 'Invalid credentials';
              break;

            default:
              message = err[1];
              break;
          }
        }
        throw message;
      });
  }

  refreshToken(token: IAuthenticationToken): Promise<IAuthenticationToken> {
    return refreshToken(this.axios, token);
  }
}

export const authenticationService = new AuthenticationService();

if (process.env.REACT_APP_API_AUTH_FAKE === 'fake') {
  const mocked = authenticationService.mock(defaultMockTime);
  const mock = mocked?.adapter;

  mock?.onPost(Uri.PostFakeRegister).reply((config) => {
    const user = JSON.parse(config['data']);

    users.push(user);

    return new Promise((resolve) => {
      resolve([200, user]);
    });
  });

  mock?.onPost(Uri.PostFakePasswordForget).reply(() => {
    // User needs to check that user is eXist or not and send mail for Reset New password
    return new Promise((resolve) => {
      resolve([200, 'Check you mail and reset your password.']);
    });
  });

  mock?.onPost(Uri.PostFakeJwtRegister).reply((config) => {
    const user = JSON.parse(config['data']);

    users.push(user);

    return new Promise((resolve) => {
      resolve([200, user]);
    });
  });

  mock?.onPost(Uri.PostEditJwtProfile).reply((config) => {
    const user = JSON.parse(config['data']);
    // const one = config.headers as AxiosHeaders;
    const validUser = users.filter((usr) => usr.uid === user.index);
    // const finalToken = one?.get('Authorization');

    return new Promise((resolve, reject) => {
      // Verify Jwt token from header.Authorization
      // if (finalToken === accessToken) {
      if (validUser['length'] === 1) {
        // Find index of specific object using findIndex method.
        const index = users.findIndex((obj) => obj.uid === user.index);

        // Update object's name property.
        users[index].username = user.username;
        resolve([200, 'Profile Updated successfully']);
      } else {
        reject([400, 'Something wrong for edit profile']);
      }
      // } else {
      //   reject([400, 'Invalid Token !!']);
      // }
    });
  });

  mock?.onPost(Uri.PostEditProfile).reply((config) => {
    const user = JSON.parse(config['data']);
    const validUser = users.filter((usr) => usr.uid === user.index);

    return new Promise((resolve, reject) => {
      if (validUser['length'] === 1) {
        // Find index of specific object using findIndex method.
        const index = users.findIndex((obj) => obj.uid === user.index);

        // Update object's name property.
        users[index].username = user.username;
        resolve([200, users[index]]);
      } else {
        reject([400, 'Something wrong for edit profile']);
      }
    });
  });

  mock?.onPost(Uri.PostFakeJwtPasswordForget).reply(() => {
    // User needs to check that user is eXist or not and send mail for Reset New password
    return new Promise((resolve) => {
      resolve([200, 'Check you mail and reset your password.']);
    });
  });
}
